<template>
  <v-container fluid class="erp-page page">
    <div class="erp-page-header page-hero">
      <div>
        <h2 class="d-flex align-center">Usuarios <HelpHint text="Administra accesos y roles de usuario. Los cambios impactan permisos de menú y operación en tiempo real." /></h2>
        <p>Gestiona accesos, roles y estado de usuarios.</p>
      </div>
      <v-btn color="primary" class="hero-btn" @click="openCreate">
        Nuevo usuario
      </v-btn>
    </div>

    <v-card outlined class="page-card">
      <div class="table-header">
        <div>
          <h3>Listado</h3>
          <p>{{ users.length }} usuarios registrados</p>
        </div>
        <v-btn text color="primary" :loading="loading" @click="fetchUsers">
          Recargar
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="users"
        :loading="loading"
        class="elevation-0"
        item-key="id"
      >
        <template v-slot:item.roles="{ item }">
          {{ formatRoles(item.roles) }}
        </template>
        <template v-slot:item.isActive="{ item }">
          <span :class="['pill', item.isActive ? 'pill--success' : 'pill--warn']">
            {{ item.isActive ? 'Activo' : 'Inactivo' }}
          </span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn text small color="primary" @click="openEdit(item)">Editar</v-btn>
          <v-btn text small color="error" :loading="deletingId === item.id" @click="removeUser(item)">Eliminar</v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="520">
      <v-card>
        <v-card-title>{{ isEditing ? 'Editar usuario' : 'Crear usuario' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-text-field
              v-model="form.email"
              label="Correo"
              outlined
              dense
              :rules="emailRules"
            />
            <v-text-field
              v-model="form.username"
              label="Usuario"
              outlined
              dense
              :rules="requiredRules"
            />
            <v-text-field
              v-model="form.password"
              :label="isEditing ? 'Nueva contraseña (opcional)' : 'Contraseña'"
              outlined
              dense
              :type="showPassword ? 'text' : 'password'"
              :append-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
              @click:append="showPassword = !showPassword"
              :rules="isEditing ? [] : requiredRules"
            />
            <v-select
              v-model="form.roleIds"
              :items="availableRoles"
              item-text="name"
              item-value="id"
              label="Roles"
              outlined
              dense
              multiple
              chips
              clearable
            />
            <v-switch
              v-if="isEditing"
              v-model="form.isActive"
              label="Usuario activo"
              inset
            />
          </v-form>
          <div v-if="error" class="error-text">{{ error }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveUser">
            Guardar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { usersService } from "../services/users";
import { rolesService } from "../services/roles";
import HelpHint from "@/components/HelpHint.vue";
import { parseApiError } from "@/utils/errors";

export default {
  name: "SecurityUsers",
  components: { HelpHint },
  data() {
    return {
      users: [],
      availableRoles: [],
      loading: false,
      dialog: false,
      isEditing: false,
      valid: false,
      saving: false,
      deletingId: "",
      error: "",
      showPassword: false,
      headers: [
        { text: "Usuario", value: "username" },
        { text: "Correo", value: "email" },
        { text: "Roles", value: "roles" },
        { text: "Estado", value: "isActive" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      form: {
        id: "",
        email: "",
        username: "",
        password: "",
        roleIds: [],
        isActive: true,
      },
      emailRules: [
        (v) => Boolean(v) || "El correo es requerido",
        (v) => /.+@.+\..+/.test(v) || "Formato de correo invalido",
      ],
      requiredRules: [(v) => Boolean(v) || "Campo requerido"],
    };
  },
  created() {
    this.loadBootstrap();
  },
  methods: {
    unwrap(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) {
        return response.data.data;
      }
      return response && response.data ? response.data : null;
    },
    async loadBootstrap() {
      this.loading = true;
      this.error = "";
      try {
        const [usersRes, rolesRes] = await Promise.all([
          usersService.getAll(),
          rolesService.getAll(),
        ]);
        this.users = this.unwrap(usersRes) || [];
        this.availableRoles = this.unwrap(rolesRes) || [];
      } catch (err) {
        this.error = parseApiError(err, "No pudimos cargar usuarios/roles.");
      } finally {
        this.loading = false;
      }
    },
    async fetchUsers() {
      this.loading = true;
      try {
        const response = await usersService.getAll();
        this.users = this.unwrap(response) || [];
      } catch (err) {
        this.error = parseApiError(err, "No pudimos cargar los usuarios.");
      } finally {
        this.loading = false;
      }
    },
    formatRoles(roles) {
      if (!roles || !roles.length) return "-";
      return roles.map((role) => role.name).join(", ");
    },
    openCreate() {
      this.isEditing = false;
      this.dialog = true;
      this.error = "";
      this.form = {
        id: "",
        email: "",
        username: "",
        password: "",
        roleIds: [],
        isActive: true,
      };
    },
    openEdit(user) {
      this.isEditing = true;
      this.dialog = true;
      this.error = "";
      this.form = {
        id: user.id,
        email: user.email,
        username: user.username,
        password: "",
        roleIds: (user.roles || []).map((role) => role.id),
        isActive: user.isActive,
      };
    },
    closeDialog() {
      this.dialog = false;
    },
    async saveUser() {
      const isValid = this.$refs.form.validate();
      if (!isValid) return;

      this.saving = true;
      this.error = "";
      const roleIds = Array.isArray(this.form.roleIds) ? this.form.roleIds : [];

      try {
        if (this.isEditing) {
          await usersService.update(this.form.id, {
            email: this.form.email,
            username: this.form.username,
            isActive: this.form.isActive,
            roleIds,
          });
          if (this.form.password) {
            await usersService.resetPassword(this.form.id, this.form.password);
          }
        } else {
          await usersService.create({
            email: this.form.email,
            username: this.form.username,
            password: this.form.password,
            roleIds,
          });
        }
        this.dialog = false;
        await this.fetchUsers();
      } catch (err) {
        this.error = parseApiError(err, "No pudimos guardar el usuario. Verifica los datos.");
      } finally {
        this.saving = false;
      }
    },
    async removeUser(user) {
      if (!window.confirm(`¿Dar de baja al usuario ${user.username || user.email}?`)) return;
      this.deletingId = user.id;
      this.error = "";
      try {
        await usersService.remove(user.id);
        await this.fetchUsers();
      } catch (err) {
        this.error = parseApiError(err, "No pudimos eliminar el usuario.");
      } finally {
        this.deletingId = "";
      }
    },
  },
};
</script>

<style scoped>
.page {
  padding: 0;
}

.page-hero {
  background: var(--bg-card);
  border-radius: 16px;
  padding: 24px 28px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-subtle);
}

.page-hero h2 {
  margin: 0 0 6px;
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.02em;
}

.page-hero p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
}

.hero-btn {
  text-transform: none;
  font-weight: 700;
}

.page-card {
  background: var(--bg-card);
  border-radius: 16px;
  padding: 20px 24px 12px;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-subtle);
}

.table-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.table-header h3 {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: -0.01em;
}

.table-header p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.pill--success {
  background: rgba(22, 163, 74, 0.12);
  color: #16a34a;
}

.pill--warn {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.error-text {
  margin-top: 12px;
  color: #dc2626;
}

@media (max-width: 960px) {
  .page-hero {
    flex-direction: column;
    align-items: flex-start;
  }
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
